import React, { useState, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import * as Types from 'types'
import { compose } from 'redux'
import { connect } from 'react-redux'
import { selectors } from '../reducer'
import * as apiActions from 'api-actions'
import { selectors as apiSelectors } from 'lp-redux-api'
import { countBy, isEqual } from 'lodash'
import { formatDate, dateSorter, displayAttribute, numberSorter } from 'utils'
import { ServicesAgreementStatus } from 'config/automatic-rollovers'
import {
  BDCombinedAgreementStatus,
  OrderStatus,
} from 'config/benefit-distributions'
import {
  PaginatedAndSortableTable,
  TableHeading,
  QuickFilterCard,
  NoTableResults,
  Spinner,
  ComponentFailure,
  StatusBadge,
  TableHeader,
  Tooltip,
  BenefitDistributionStatusModal,
  DownloadTableButton,
} from 'components'
import { TableColumn } from 'lp-components'
import { Icon as SuccessIcon } from 'images/status-icons/checkmark-green.svg'
import { Icon as NotAcceptedIcon } from 'images/status-icons/x-with-circle.svg'
import { Icon as FundingIcon } from 'images/status-icons/cheque.svg'
import { Icon as InProgressIcon } from 'images/status-icons/clock-new.svg'
import { Icon as FileRecievedIcon } from 'images/status-icons/document.svg'
import { Icon as QuestionIcon } from 'images/questionmark-circle.svg'

const propTypes = {
  bdPlanAgreement: Types.bdPlanAgreementsSummary.isRequired,
  benefitDistributionOrders: PropTypes.arrayOf(Types.benefitDistributionOrder)
    .isRequired,
  fetchBenefitDistributionOrders: PropTypes.func.isRequired,
  isFetchingBenefitDistributionOrders: PropTypes.bool.isRequired,
  servicesAgreementId: PropTypes.string.isRequired,
  baseFilters: PropTypes.shape({
    status: PropTypes.arrayOf(PropTypes.string).isRequired,
  }).isRequired,
  filters: PropTypes.shape({
    status: PropTypes.arrayOf(PropTypes.string).isRequired,
  }).isRequired,
  handleFilter: PropTypes.func.isRequired,
  fieldMappings: PropTypes.arrayOf(PropTypes.object).isRequired,
}

const defaultProps = {}

const TABLE_ID = 'bd-orders-table'

const OrdersHeader = ({ children }) => {
  return (
    <div className="services-agreement-participants-header">
      <h2>My distributions</h2>
      {children}
    </div>
  )
}

function BenefitDistributionOrders({
  bdPlanAgreement,
  benefitDistributionOrders,
  fetchBenefitDistributionOrders,
  isFetchingBenefitDistributionOrders,
  servicesAgreementId,
  baseFilters,
  filters,
  handleFilter,
  fieldMappings,
}) {
  const [errorExists, setErrorExists] = useState(false)
  const [showBDStatusModal, setShowBDStatusModal] = useState(false)
  const hasFiltersApplied = !isEqual(filters, baseFilters)

  const { addendumStatus } = bdPlanAgreement

  useEffect(() => {
    async function getBenefitDistributionOrders() {
      try {
        if (
          addendumStatus === ServicesAgreementStatus.COMPLETED ||
          addendumStatus === BDCombinedAgreementStatus.COMBINED_COMPLETED
        ) {
          await fetchBenefitDistributionOrders(servicesAgreementId)
        }
      } catch {
        setErrorExists(true)
      }
    }
    // will need to add trigger-refetch logic
    if (!benefitDistributionOrders) getBenefitDistributionOrders()
  }, [benefitDistributionOrders])

  const noBenefitDistributionOrders = !benefitDistributionOrders?.length

  const toggleFilter = (filterLabel) => {
    handleFilter({
      status: filters.status.includes(filterLabel)
        ? filters.status.filter((status) => status !== filterLabel)
        : filters.status.concat(filterLabel),
    })
  }

  const counts = countBy(benefitDistributionOrders, 'distributionStatus')

  const filteredBDOrders = useMemo(() => {
    if (!benefitDistributionOrders) return
    let searchResults = benefitDistributionOrders

    if (hasFiltersApplied) {
      return searchResults.filter(({ distributionStatus }) =>
        filters.status.includes(distributionStatus)
      )
    }

    return searchResults
  }, [benefitDistributionOrders, filters, hasFiltersApplied])

  if (isFetchingBenefitDistributionOrders)
    return (
      <div className="services-agreement-participants">
        <OrdersHeader />
        <Spinner />
      </div>
    )

  if (errorExists)
    return (
      <div className="services-agreement-participants">
        <OrdersHeader />
        <ComponentFailure />
      </div>
    )

  const disabled = !filteredBDOrders?.length
  const showIcon = disabled

  return (
    <div className="services-agreement-participants">
      <hr />
      <div className="quick-filters-container">
        <div className="contaioner">
          <div className="flex-columns">
            <div className="flex-column">
              <QuickFilterCard
                controlId={TABLE_ID}
                isActive={filters.status.includes(OrderStatus.FILE_RECIEVED)}
                count={counts[OrderStatus.FILE_RECIEVED] ?? 0}
                label={OrderStatus.FILE_RECIEVED}
                icon={FileRecievedIcon}
                onClick={() => toggleFilter(OrderStatus.FILE_RECIEVED)}
              />
            </div>
            <div className="flex-column">
              <QuickFilterCard
                controlId={TABLE_ID}
                isActive={filters.status.includes(
                  OrderStatus.READY_FOR_FUNDING
                )}
                count={counts[OrderStatus.READY_FOR_FUNDING] ?? 0}
                label={OrderStatus.READY_FOR_FUNDING}
                icon={FundingIcon}
                onClick={() => toggleFilter(OrderStatus.READY_FOR_FUNDING)}
              />
            </div>
            <div className="flex-column">
              <QuickFilterCard
                controlId={TABLE_ID}
                isActive={filters.status.includes(OrderStatus.IN_PROGRESS)}
                count={counts[OrderStatus.IN_PROGRESS] ?? 0}
                label={OrderStatus.IN_PROGRESS}
                icon={InProgressIcon}
                onClick={() => toggleFilter(OrderStatus.IN_PROGRESS)}
              />
            </div>
            <div className="flex-column">
              <QuickFilterCard
                controlId={TABLE_ID}
                isActive={filters.status.includes(OrderStatus.COMPLETED)}
                count={counts[OrderStatus.COMPLETED] ?? 0}
                label={OrderStatus.COMPLETED}
                icon={SuccessIcon}
                onClick={() => toggleFilter(OrderStatus.COMPLETED)}
              />
            </div>
            <div className="flex-column">
              <QuickFilterCard
                controlId={TABLE_ID}
                isActive={filters.status.includes(OrderStatus.NOT_ACCEPTED)}
                count={counts[OrderStatus.NOT_ACCEPTED] ?? 0}
                label={OrderStatus.NOT_ACCEPTED}
                icon={NotAcceptedIcon}
                onClick={() => toggleFilter(OrderStatus.NOT_ACCEPTED)}
              />
            </div>
          </div>
        </div>
      </div>

      <TableHeading title="My distributions">
        {!noBenefitDistributionOrders && (
          <DownloadTableButton
            data={filteredBDOrders}
            fieldMappings={fieldMappings}
            fileName="Inspira Financial_Retirement Services_BD Orders"
            disabled={disabled}
            showIcon={showIcon}
          />
        )}
      </TableHeading>
      <PaginatedAndSortableTable
        tableId={TABLE_ID}
        data={filteredBDOrders}
        initialAscending={false}
        initialColumn="createdDate"
        parentScrollContainerSelector="#main-content"
        className="services-agreements-table"
        noRowsFoundMessage={
          <NoTableResults
            title="No results found"
            details={
              noBenefitDistributionOrders ? (
                <>
                  When an order is added, we'll send you a notification.
                  <br />
                  You could also try adjusting your filter.
                </>
              ) : (
                <>Try adjusting your filter.</>
              )
            }
          />
        }
      >
        <TableColumn
          name="orderNumber"
          label="Order number"
          format={displayAttribute}
          sortFunc={numberSorter}
        />
        <TableColumn
          name="distributionStatus"
          label="Distribution status"
          headerComponent={(props) => (
            <TableHeader {...props}>
              <Tooltip
                content="To learn more about distribution statuses click on the question mark."
                ariaLabel="Distribution status"
                plugins={[]}
              >
                <QuestionIcon onClick={() => setShowBDStatusModal(true)} />
              </Tooltip>
            </TableHeader>
          )}
          format={displayAttribute}
          component={({ data, value }) => {
            const { distributionStatus } = data
            return (
              <td className="status">
                {distributionStatus ? (
                  <StatusBadge status={distributionStatus} />
                ) : (
                  <span>{value}</span>
                )}
              </td>
            )
          }}
        />
        <TableColumn
          name="createdDate"
          label="Submission date"
          format={formatDate}
          sortFunc={dateSorter}
        />
      </PaginatedAndSortableTable>
      {showBDStatusModal && (
        <BenefitDistributionStatusModal
          onClose={() => setShowBDStatusModal(false)}
        />
      )}
    </div>
  )
}

BenefitDistributionOrders.propTypes = propTypes
BenefitDistributionOrders.defaultProps = defaultProps

function mapStateToProps(state, { servicesAgreementId }) {
  return {
    isFetchingBenefitDistributionOrders: apiSelectors.isLoading(
      state,
      apiActions.fetchBenefitDistributionOrders
    ),
    benefitDistributionOrders: selectors.benefitDistributionOrders(state, {
      servicesAgreementId,
    }),
  }
}

const mapDispatchToProps = {
  fetchBenefitDistributionOrders: apiActions.fetchBenefitDistributionOrders,
}

export default compose(connect(mapStateToProps, mapDispatchToProps))(
  BenefitDistributionOrders
)
